<script setup>
import { WdsBreadCrumbs } from '@wds/components';
import { SEGMENT_PAGE_DATA } from '@/config/analytics';
import { APPDOWNLOADBANNERLINKS } from '@/config/app-download-banner-links.js';
import { commonValues } from '@/config/common';
import { TRACKING_EMPTY } from '@/tracking/variables.js';
import { getHouseRulesTracking } from '@/utils/houseRulesTracking.js';
import { findPropertyTypeByKey } from '@/utils/propertyTypes';

definePageMeta({ middleware: 'validate-property' });

const {
  $tracking,
  $url,
} = useNuxtApp();
const {
  locale,
  t,
} = useI18n();

// ### COMPOSABLES
const route = useRoute();
const dateTime = useDateTime();
const { useRouteManager } = useRoutes();
const { useUrl } = useUrls();
const { buildSeo } = generateSeo();
const { definePropertySchema } = generateSchemas();
const { useImage } = useImages();
const { useRating } = useRatings();
const { socialCues } = useSocialCues();
const { usePropertyList } = useProperties();
const {
  isExtraSmallScreen,
  isBelowMediumScreen,
  isSmallOrMediumScreen,
}
  = useMq();

// ### COMPOSABLES - APIs
const {
  getCityById,
  getCityProperties,
  getCityReviewSummaries,
  getTopPropertiesByCityId,
} = useApiCities();
const {
  getPropertyPageTags,
  getCityPageTags,
} = useApiSeo();
const { getPropertyLocationTranslatedById } = useApiTranslator();
const { getSustainabilityLevel } = useSustainability();
const { hasOgEventsCityContent } = useOgEvents();

// ### STORES
const languageStore = useLanguageStore();
const sessionStore = useSessionStore();
const locationStore = useLocationStore();
const searchStore = useSearchStore();
const testingStore = useTestingStore();

// ### A/B TESTING FEATURE TOGGLES
const experimentLinkups = testingStore.hasEnabledFeature('web_linkupsCallouts');
const experimentPdpOgEvents = testingStore.hasEnabledFeature(
  'web_pdpOGEventsCarousel',
);
const experimentSocialCues = testingStore.hasEnabledFeature(
  'web_socialCuesCallouts',
);
const experimentSocialCuesParticipating = testingStore.hasEnabledFeature(
  'web_isUserParticipatingSocialCuesCallouts',
);
const experimentPropertyPageMetaTags = testingStore.hasEnabledFeature(
  'static_propertypage_metatags',
);
const experimentReviewsSummary = testingStore.hasEnabledFeature(
  'web_enableAIReviewSummaryOnStaticPDP',
);

// ### CONSTANTS INITIALIZATION
const locationKey = 'property';
const entryPageName = 'PROPERTY';
const gtmPageName = 'staticMicrosite';
const gtmClassification = 'microsite';
const segmentTracking = SEGMENT_PAGE_DATA.property;
const cityObj = {};

const urlParams = route?.params || null;
const urlQuery = route?.query || null;
const AUTO_GENERATED_TEXT_STRING = '(Auto-translated from original language)';

// ### VARIABLES INITIALIZATION
const propertyLinkupEventsSection = ref(null);
const propertyDetailsActiveTab = ref(null);
const isPropertyDetailsOpen = ref(false);
const isLocationModalOpen = ref(false);
const isReviewsModalOpen = ref(false);
const isSocialCuesModalOpen = ref(false);

// ### ROUTE VALIDATION
const propertyTypeObj = useRouteManager.validatePropertyTypeParam(
  urlParams.propertyType,
);
const propertyTypeKey = propertyTypeObj?.key;

const propertyData = await useRouteManager.validatePropertyParam(
  propertyTypeObj,
  urlParams.propertyId,
);
const urlData = useRouteManager.getUrlData(
  urlParams.propertyType,
  null,
  null,
  null,
  null,
  urlParams.propertyId,
  urlParams.propertyName,
);
useRouteManager.redirectRoute(urlData, urlQuery);

// ### PROPERTY OG EVENTS ELIGIBILITY + PROPERTY HREFLANGS
// Independent calls resolved in parallel to avoid an SSR waterfall.
const isOgEventsCandidate = experimentPdpOgEvents && locale?.value === 'en';
const [hasOgEventsContent, urlTranslations] = await Promise.all([
  isOgEventsCandidate
    ? hasOgEventsCityContent(propertyData?.location?.city?.id)
    : Promise.resolve(false),
  getPropertyLocationTranslatedById(propertyData?.id),
]);
const isOgEventsEligible = isOgEventsCandidate && hasOgEventsContent;

// ### PROPERTY CANONICAL URL
const propertyPageUrl = useUrl.getPropertyPageUrl({
  propertyTypeSlug: propertyTypeObj?.slug,
  propertyId: propertyData?.id,
  urlFriendlyProperty: propertyData?.urlFriendlyName,
});

const pageCanonicalUrl = propertyPageUrl;
const hrefUrls = useUrl.generateHrefLangForProperty(
  urlTranslations,
  propertyTypeObj.key,
  propertyData.id,
);

// ### PROPERTY MAIN IMAGE
const propertyMainImage = propertyData?.images?.length
  ? propertyData?.images[0]
  : null;

/*
// @TODO NOT NEEDED AT THE MOMENT, BUT WILL BE NEEDED FOR SCHEMAS
// ### PROPERTY MINIMUM PRICE
const propertyMinPrice
  = propertyData.lowestPrice && propertyData.currency
    ? `${t('t_SCHEMAPRICESFROM')} ${useFormatter.formatCurrency(propertyData.lowestPrice, propertyData.currency)}`
    : '';
*/

// ### PROPERTY STATIC MAP PREVIEW (Mapbox or MapTiler via web_enableMapTiler)
const { getStaticMapImageUrl } = useMapStaticImage();
const propertyMapLink = getStaticMapImageUrl({
  longitude: propertyData?.location?.geoCoordinates?.longitude,
  latitude: propertyData?.location?.geoCoordinates?.latitude,
  zoom: 14,
  width: 300,
  height: 300,
  bearing: 0,
});

// ### PROPERTY BREADCRUMBS
const crumbs = [
  {
    label: t('t_A11YBCTITLEPROPERTYTYPE', { PROPERTYTYPE: t(propertyTypeObj?.transcode) }),
    title: t(propertyTypeObj.transcode),
    link: useUrl.getWorldPageUrl({ propertyTypeSlug: propertyTypeObj?.slug }),
  },
  {
    label: t('t_A11YBCTITLELOCATION', {
      LOCATIONNAME: propertyData?.location?.continent?.name,
      PROPERTYTYPE: t(propertyTypeObj?.transcode),
    }),
    title: t('t_A11YBCTEXTLOCATION', {
      LOCATIONNAME: propertyData?.location?.continent?.name,
      PROPERTYTYPE: t(propertyTypeObj?.transcode),
    }),
    link: useUrl.getContinentPageUrl({
      propertyTypeSlug: propertyTypeObj?.slug,
      urlFriendlyContinent: propertyData?.location?.continent?.urlFriendlyName,
    }),
  },
  {
    label: t('t_A11YBCTITLELOCATION', {
      LOCATIONNAME: propertyData?.location?.country?.name,
      PROPERTYTYPE: t(propertyTypeObj?.transcode),
    }),
    title: t('t_A11YBCTEXTLOCATION', {
      LOCATIONNAME: propertyData?.location?.country?.name,
      PROPERTYTYPE: t(propertyTypeObj?.transcode),
    }),
    link: useUrl.getCountryPageUrl({
      propertyTypeSlug: propertyTypeObj?.slug,
      urlFriendlyContinent: propertyData?.location?.continent?.urlFriendlyName,
      urlFriendlyCountry: propertyData?.location?.country?.urlFriendlyName,
    }),
  },
  {
    label: t('t_A11YBCTITLELOCATION', {
      LOCATIONNAME: propertyData?.location?.city?.name,
      PROPERTYTYPE: t(propertyTypeObj?.transcode),
    }),
    title: t('t_A11YBCTEXTLOCATION', {
      LOCATIONNAME: propertyData?.location?.city?.name,
      PROPERTYTYPE: t(propertyTypeObj?.transcode),
    }),
    link: useUrl.getCityPageUrl({
      propertyTypeSlug: propertyTypeObj?.slug,
      urlFriendlyContinent: propertyData?.location?.continent?.urlFriendlyName,
      urlFriendlyCountry: propertyData?.location?.country?.urlFriendlyName,
      urlFriendlyCity: propertyData?.location?.city?.urlFriendlyName,
    }),
  },
  {
    link: '#',
    title: propertyData.name,
  },
];

// ### MORE PROPERTIES - Other properties in the same city as the current property
const topPropertiesInCity = await getTopPropertiesByCityId(
  propertyTypeKey,
  propertyData?.location?.city?.id,
);
const morePropertiesInCity
  = topPropertiesInCity?.filter((c) => c.id !== propertyData.id)?.slice(0, 6)
    || [];

// ### PROPERTY SEO DATA
const seoData = await getPropertyPageTags(propertyTypeObj.key, propertyData.id);

if (experimentPropertyPageMetaTags && seoData) {
  seoData.title = t('t_DEFAULTSEOPROPERTYPAGETITLE');
  seoData.ogTitle = t('t_DEFAULTSEOPROPERTYPAGETITLE');
  seoData.description = t('t_DEFAULTSEOPROPERTYPAGEDESCRIPTION');
  seoData.ogDescription = t('t_DEFAULTSEOPROPERTYPAGEDESCRIPTION');
}

const seoExtraData = {
  canonicalUrl: pageCanonicalUrl,
  ogImage: useImage.getImage(propertyMainImage?.medium) || '',
};
const tradReplacements = {
  PROPERTYNAME: propertyData?.name,
  LOCATIONNAME: propertyData?.name,
  CITYNAME: propertyData?.location?.city?.name,
  CITY: propertyData?.location?.city?.name,
  REVIEWSCOUNT: propertyData?.reviews?.totalCount || '',
  COUNTRYNAME: propertyData?.location?.country?.name,
  COUNTRY: propertyData?.location?.country?.name,
  PROPERTYTYPE: t(propertyTypeObj?.transcode_s),
  PROPERTYTYPES: t(propertyTypeObj?.transcode),
  SERVERNAME: commonValues.HOSTELWORLD,
  YEAR: dateTime.today.year,
};
const seoMetaData = buildSeo.getMetaData(
  seoData,
  seoExtraData,
  tradReplacements,
  false,
  locationKey,
);

// CITY INFO (for About City section)
if (propertyData?.location?.city?.id) {
  const cityId = propertyData?.location?.city?.id;
  const [citySeoData, reviewSummaries, cityInfo] = await Promise.all([
    getCityPageTags(propertyTypeKey, cityId),
    getCityReviewSummaries(cityId),
    getCityById(cityId),
  ]);
  const cityAllProperties = await getCityProperties(propertyTypeKey, cityId, 1, 30, 0, null, cityInfo);

  cityObj.ratingBreakdown = reviewSummaries?.ratings || {};
  cityObj.id = cityId;
  cityObj.name = propertyData?.location?.city?.name;

  usePropertyList.setPropertyList(cityAllProperties?.properties);
  const cityTopCheapestPropertiesPlaceholders = usePropertyList.getTopCheapestPropertiesForPlaceholders.value || [];
  const cityTopRankedPropertiesPlaceholders = usePropertyList.getTopRankedPropertiesForPlaceholders.value || [];

  const countrySearchUrl = useUrl.getCountryPageUrl({
    propertyTypeSlug: propertyTypeObj?.slug,
    urlFriendlyContinent: propertyData?.location?.continent?.urlFriendlyName,
    urlFriendlyCountry: propertyData?.location?.country?.urlFriendlyName,
  });

  const tradReplacementsForCity = {
    CHOSENCITY: propertyData?.location?.city?.name,
    CHOSENCONTINENT: propertyData?.location?.continent?.name,
    CHOSENCOUNTRY: propertyData?.location?.country?.name,
    CITY: propertyData?.location?.city?.name,
    CITYNAME: propertyData?.location?.city?.name,
    COUNTRY: propertyData?.location?.country?.name,
    COUNTRYNAME: propertyData?.location?.country?.name,
    COUNTRYSEARCHURL: countrySearchUrl,
    LOCATIONNAME: cityObj?.name,
    PROPCOUNT: cityAllProperties?.totalPropertiesCount || '',
    PROPERTYNAME: propertyData?.name,
    PROPERTYTYPE: t(propertyTypeObj?.transcode_s),
    PROPERTYTYPES: t(propertyTypeObj?.transcode),
    REVIEWCOUNT: cityInfo?.numberOfReviews || '',
    REVIEWSCOUNT: propertyData?.reviews?.totalCount || '',
    SERVERNAME: commonValues.HOSTELWORLD,
    YEAR: dateTime.today.year,
  };

  // ### ADD CITY TOP RANKED PROPERTIES TO PLACEHOLDER REPLACEMENTS
  if (Array.isArray(cityTopRankedPropertiesPlaceholders) && cityTopRankedPropertiesPlaceholders.length) {
    Object.assign(tradReplacementsForCity, ...cityTopRankedPropertiesPlaceholders);
  }

  // ### ADD CITY TOP CHEAPEST PROPERTIES TO PLACEHOLDER REPLACEMENTS
  if (Array.isArray(cityTopCheapestPropertiesPlaceholders) && cityTopCheapestPropertiesPlaceholders.length) {
    Object.assign(tradReplacementsForCity, ...cityTopCheapestPropertiesPlaceholders);
  }

  const citySeoMetaData = buildSeo.getMetaData(
    citySeoData,
    {},
    tradReplacementsForCity,
  );

  const aboutCityDescription = $url.processHtmlContent(citySeoMetaData?.copy);
  cityObj.description
    = aboutCityDescription?.processedContent || citySeoMetaData?.copy;
}

// ### COMPUTED
const getPropertyFlatFacilites = computed(() => {
  return (
    propertyData?.facilities?.flatMap((item) => [...item.facilities]) || []
  );
});

const propertySortedOgGatherings = computed(() => {
  return sortAndLimitOgGatherings(propertyData?.ogGatherings?.items);
});

const eventsCarousel = useEventsCarousel({
  linkupsItems: computed(() => propertyData?.linkups?.items || []),
  ogEventsItems: propertySortedOgGatherings,
  linkupsTotalCount: computed(() => propertyData?.linkups?.totalCount),
  ogEventsTotalCount: computed(() => propertyData?.ogGatherings?.totalCount),
  showOgEventsModule: isOgEventsEligible,
  propertyLocation: computed(() => propertyData?.location),
  propertyId: computed(() => propertyData?.id),
  propertyName: computed(() => propertyData?.name),
  propertyPageUrl: computed(() => propertyPageUrl),
});

const showPropertyLinkupEventsCarousel = computed(() => {
  return experimentLinkups && eventsCarousel.showEventsCarousel.value;
});

function getCarouselTrackingSegmentParams() {
  const occasionGeniusEvents = eventsCarousel.occasionGeniusEventsInCarousel.value;

  return {
    carousel_flag: showPropertyLinkupEventsCarousel.value,
    linkups_count_displayed: eventsCarousel.linkupsCountDisplayed.value,
    og_events_count_display: eventsCarousel.ogEventsCountDisplayed.value,
    ...(occasionGeniusEvents ? { occasion_genius_events: occasionGeniusEvents } : {}),
  };
}

const pdpCarouselDisplayEvents = computed(
  () => eventsCarousel.carouselDisplayEvents.value,
);
const pdpCarouselTotalCount = computed(
  () => eventsCarousel.totalEventCount.value,
);
const pdpCarouselShowFilterPills = computed(
  () => eventsCarousel.showFilterPills.value,
);
const pdpCarouselActiveFilter = computed(
  () => eventsCarousel.activeEventFilter.value,
);
const pdpCarouselOgEventsCount = computed(
  () => eventsCarousel.ogEventsCountDisplayed.value,
);
const pdpCarouselLinkupsCount = computed(
  () => eventsCarousel.linkupsCountDisplayed.value,
);

const getPropertyHeadingData = computed(() => {
  const propertyTypeInfo = findPropertyTypeByKey(propertyData.type);

  // HOSCARS WINNER
  const hoscarWinnerList = propertyData?.hoscars?.awards || [];
  const isHoscarWinnerForCurrentYear
    = hoscarWinnerList.findIndex((award) => award.year === dateTime.today.year)
      >= 0;
  const hoscarWinnerForCurrentYearTitle = isHoscarWinnerForCurrentYear
    ? `${dateTime.today.year} Hoscars ${t('t_WINNER')}`
    : false;

  // LINKUPS
  const propertyLinkupsForHeading = {
    isClickable: true,
    totalCount: pdpCarouselTotalCount.value,
    items: eventsCarousel.headingPreviewImages.value,
  };

  return {
    propertyId: propertyData.id,
    propertyType: t(propertyTypeInfo.transcode_s),
    propertyName: propertyData?.name,
    propertyLocation: propertyData?.location,
    propertyRating: {
      averageRating: propertyData?.reviews?.totalAverageRating,
      numberOfReviews: propertyData?.reviews?.totalCount,
    },
    propertySustainability: getSustainabilityLevel(
      propertyData?.sustainability,
    ),
    propertyBadges: {
      awardWinner: hoscarWinnerForCurrentYearTitle,
      recommendedByHw: propertyData?.recommendedByHw
        ? t('t_RECOMMENDED')
        : false,
      freeWifi: propertyData?.badges?.find((b) => b.id === '90'),
      breakFastIncluded: propertyData?.badges?.find((b) => b.id === '1'),
    },
    propertyTotalFacilities: getPropertyFlatFacilites.value?.length || 0,
    propertyCanxPolicyDays: propertyData?.policies?.freeCancellation
      ?.isAvailable
      ? propertyData?.policies?.freeCancellation?.daysInAdvance
      : 0,
    propertyLinkups: propertyLinkupsForHeading,
    propertyWhosGoing: propertyData?.whosGoing,
  };
});

const getPropertyFacilitiesSnippetData = computed(() => {
  return { facilities: getPropertyFlatFacilites.value.slice(0, 8) || [] };
});

const propertyHasFacilities = computed(() => {
  return toValue(getPropertyFacilitiesSnippetData)?.facilities?.length;
});

const getPropertyHouseRulesData = computed(() => {
  return {
    checkInTime: propertyData?.policies?.checkIn,
    checkOutTime: propertyData?.policies?.checkOut,
    additionalRules: propertyData?.policies?.additional,
    otherHouseRules: propertyData?.policies?.rules || null,
  };
});

const getMorePropertiesInCityListing = computed(
  () => morePropertiesInCity || [],
);

const hasRatingAndReviews = computed(() => {
  return (
    propertyData?.reviews?.totalAverageRating > 0
    && propertyData?.reviews?.latest?.length
  );
});

const getPropertyRatingAndReviewsData = computed(() => {
  return {
    propertyId: propertyData.id,
    propertyRating: {
      averageRating: propertyData?.reviews?.totalAverageRating,
      numberOfReviews: propertyData?.reviews?.totalCount,
      breakdown: propertyData?.reviews?.averageRatings,
    },
    propertyReviewsSummary:
      typeof propertyData?.reviews?.AIReviewsSummary === 'string'
        ? propertyData.reviews.AIReviewsSummary.trim()
        : '',
    propertyLatestReviews: propertyData?.reviews?.latest?.map((review) => {
      const reviewScore = review.rating || 0;
      const reviewDescription = [
        t(`t_${review.guestGender.toUpperCase().replace(/ /g, '')}`) || '',
      ];
      reviewDescription.push(review.guestAgeGroup || '');
      reviewDescription.push(review.guestCountry || '');
      const reviewDate = review.stayed
        ? dateTime.formatToHumanReadableShortDate(
          review.stayed,
          languageStore.getCurrentLanguage.language,
          'month|year',
        )
        : '';

      return {
        date: `${t('t_STAYEDINDATE', { DATE: reviewDate })}`,
        text: review.review,
        rating: Number(reviewScore.toFixed(1)),
        author: {
          userName: review.guestFirstname || '',
          description: reviewDescription.join(', '),
          countryCode: review.guestCountryCode,
          countryName: review.guestCountry,
          ageRange: review.guestAgeGroup,
          groupTypeCode: review.guestGender,
          flagURL: review.guestCountryCode
            ? `https://a.hwstatic.com/hw/flags/${review.guestCountryCode}.svg`
            : null,
          color: 'pink',
        },
      };
    }),
  };
});

const propertyDetailsTabsData = computed(() => {
  const propertyFullAddress
    = [
      propertyData?.location?.address?.line1,
      propertyData?.location?.address?.line2,
      propertyData?.location?.address?.postalCode,
    ]
      .filter(Boolean)
      .join(', ') || '';

  return {
    about: { description: propertyData?.descriptions?.main || '' },
    facilities: propertyData?.facilities,
    houserules: {
      ...toValue(getPropertyHouseRulesData),
      thingsToNote: propertyData?.notes,
      cancellationPolicy: propertyData?.policies?.cancellationPolicy,
    },
    hoscars: propertyData?.hoscars,
    sustainability: getSustainabilityLevel(propertyData?.sustainability),
    businessDetails: {
      propertyName: propertyData?.name,
      propertyAddress: propertyFullAddress,
      propertyCity: propertyData?.location?.city?.name,
      propertyCountry: propertyData?.location?.country?.name,
      propertyCountryId: propertyData?.location?.country?.id,
      propertyRegisterNumber: propertyData?.businessNumber,
    },
  };
});

const getCrumbsShownByViewport = computed(() => {
  return toValue(isExtraSmallScreen) ? 2 : null;
});

const getAppDownloadDetails = computed(() => {
  const searchParamsCheckIn = dateTime.formatToFlat(
    searchStore.getSearchCheckIn,
  );
  const searchParamsCheckOut = dateTime.formatToFlat(
    searchStore.getSearchCheckOut,
  );

  const searchParamsGroupType = searchStore.getSearchGroupType?.value || null;

  let appLink = `py/${propertyData.id}?from=${searchParamsCheckIn}&to=${searchParamsCheckOut}&guests=${searchStore.getSearchNumberOfGuests}`;

  if (searchParamsGroupType) {
    appLink += `&groupType=${searchParamsGroupType}&groupAgeRange=${searchStore.getSearchGroupAge}`;
  }

  return {
    page: 'property',
    linkPaths: APPDOWNLOADBANNERLINKS?.PROPERTY,
    linkParams: appLink,
  };
});

const galleryHasImages = computed(() => {
  return propertyData?.images?.length;
});

// ### METHODS
function togglePropertySectionPopup(tab = null) {
  switch (tab) {
    case 'social-cues':
      isSocialCuesModalOpen.value = !isSocialCuesModalOpen.value;
      if (isSocialCuesModalOpen.value && experimentSocialCuesParticipating) {
        $tracking.triggerSocialCuesEventAction({
          action: 'Social Cues Counter Clicked',
          social_cues_available: propertyData?.whosGoing?.total > 0,
          social_cues_web_cohort: experimentSocialCues
            ? 'variation'
            : 'control',
          social_cues_counter_flag:
            experimentSocialCues && propertyData?.whosGoing?.total > 0,
          social_cues_count_displayed: socialCues.getSocialCuesCount(propertyData?.whosGoing?.total),
        });
      }
      break;
    case 'location':
      isLocationModalOpen.value = !isLocationModalOpen.value;
      break;
    case 'reviews':
      isReviewsModalOpen.value = !isReviewsModalOpen.value;
      break;
    case null:
      isPropertyDetailsOpen.value = false;
      break;
    default:
      propertyDetailsActiveTab.value = tab;
      isPropertyDetailsOpen.value = true;
      break;
  }
}

function handleLinkupsEventsClick() {
  const propertyLinkupEventsSectionElem
    = propertyLinkupEventsSection?.value?.$el;
  if (propertyLinkupEventsSectionElem) {
    propertyLinkupEventsSectionElem.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    $tracking.triggerLinkupEventAction({
      action: 'Linkups Counter Clicked',
      linkups_web_cohort: experimentLinkups ? 'variation' : 'control',
      linkups_counter_flag: experimentLinkups,
      linkups_carousel_flag: experimentLinkups,
      linkups_count_displayed: eventsCarousel.linkupsCountDisplayed.value,
      og_events_carousel_flag: isOgEventsEligible,
      og_events_count: eventsCarousel.ogEventsCountDisplayed.value,
      events_count_displayed: eventsCarousel.totalEventCount.value,
      og_events_web_cohort: experimentPdpOgEvents ? 'variation' : 'control',
      ...getCarouselTrackingSegmentParams(),
    });
  }
}

useHead(() => ({
  htmlAttrs: { lang: locale?.value },
  link: [
    {
      rel: 'canonical',
      href: seoMetaData?.canonicalUrl,
    },
  ].concat(hrefUrls),
}));

useSeoMeta({
  title: () => seoMetaData?.title,
  ogTitle: () => seoMetaData?.ogTitle,
  description: () => seoMetaData?.description,
  ogDescription: () => seoMetaData?.ogDescription,
  ogUrl: () => seoMetaData?.canonicalUrl,
  ogImage: () => seoMetaData?.ogImage,
  ogImageAlt: () => seoMetaData?.ogDescription,
  ogType: () => 'website',
  keywords: () => seoMetaData?.keywords,
  robots: () => 'index, follow, maxImagePreview:large',
  twitterCard: () => 'summary_large_image',
  twitterTitle: () => seoMetaData?.ogTitle,
  twitterDescription: () => seoMetaData?.ogDescription,
  twitterImage: () => seoMetaData?.ogImage,
  twitterImageAlt: () => seoMetaData?.ogDescription,
});

// ### LOCATION
locationStore.setLocation(locationKey, {
  id: propertyData?.id,
  name: `${propertyData?.name}, ${propertyData?.location?.city?.name}, ${propertyData?.location?.country?.name}`,
  slug: propertyData?.urlFriendlyName,
});
locationStore.setLocation('city', {
  id: propertyData?.location?.city?.id,
  name: `${propertyData?.location?.city?.name}`,
});

// ### SCHEMAS
const schemaCrumbs = crumbs?.map((item) => {
  return {
    name: item?.title,
    item: item?.link,
  };
});

useSchemaOrg([
  defineBreadcrumb({
    '@id': '#hostelworldNavigation',
    'name': 'Breadcrumb',
    'itemListElement': schemaCrumbs,
  }),
  definePropertySchema(
    propertyData,
    propertyTypeObj,
    seoMetaData,
    propertyMapLink,
  ),
]);

// ### TRACKING
sessionStore.setEntryPoint(entryPageName);
const houseRulesTracking = getHouseRulesTracking(propertyData?.policies);

// ### MOUNTED
onMounted(() => {
  const trackingPropertyDescriptionAutoTranslated
    = propertyData?.descriptions?.main?.includes(AUTO_GENERATED_TEXT_STRING);

  const trackingSearchCheckInDate = dateTime.formatToFlat(
    searchStore.getSearchCheckIn,
  );
  const trackingSearchCheckInDateStripped = trackingSearchCheckInDate.replace(
    /-/g,
    '',
  );
  const trackingSearchCheckOutDate = dateTime.formatToFlat(
    searchStore.getSearchCheckOut,
  );
  const trackingSearchCheckOutDateStripped = trackingSearchCheckOutDate.replace(
    /-/g,
    '',
  );

  const trackingPropertyClassName = useRating
    .getRatingClass(
      propertyData?.reviews?.totalAverageRating,
      propertyData.isNew,
    )
    .toLowerCase();
  const trackingPropertyDepositPercentage = propertyData.depositPercentage;
  const trackingPropertyType = propertyTypeObj.type.toLowerCase();

  const trackingInfo = {
    gtm: {
      gtmPageName,
      gtmClassification,
      gtmPropertyType: trackingPropertyType,
      gtmCanonicalUrl: seoExtraData.canonicalUrl,
      gtmContinent: propertyData?.tracking?.continent,
      gtmCountry: propertyData?.tracking?.country,
      gtmCity: propertyData?.tracking?.city,
      gtmPropertyName: propertyData.name,
      gtmPropertyClass: trackingPropertyClassName,
      gtmHostelName: propertyData.name,
      gtmHostelNumber: propertyData.id,
      gtmDepositServiceFee: trackingPropertyDepositPercentage,
      gtmSearchKeywords: searchStore.getSearchPhrase,
      gtmArrivalDate: trackingSearchCheckInDate,
      gtmArrivalDateStripped: trackingSearchCheckInDateStripped,
      gtmDepartureDate: trackingSearchCheckOutDate,
      gtmDepartureDateStripped: trackingSearchCheckOutDateStripped,
      gtmPropertyIds: propertyData.id,
      gtmRoomTypes: 'zero',
      gtmPrivateDormSplit: 'zero',
    },
    segment: {
      event_name: segmentTracking.VIEWED,
      page_type: segmentTracking.TYPE,
      property_type: trackingPropertyType,
      destination_city: propertyData?.tracking?.city,
      destination_city_id: propertyData?.location?.city?.id?.toString(),
      destination_country: propertyData?.tracking?.country,
      destination_continent: propertyData?.tracking?.continent,
      product_id: propertyData.id?.toString(),
      sku: propertyData.id?.toString(),
      property_name: propertyData?.tracking?.name,
      property_deposit_percent:
        trackingPropertyDepositPercentage || TRACKING_EMPTY,
      property_class: trackingPropertyClassName || TRACKING_EMPTY,
      property_price_from: propertyData?.lowestPricePerNight?.value || 0,
      property_currency: propertyData?.currency,
      position: TRACKING_EMPTY,
      machine_translation_flag: trackingPropertyDescriptionAutoTranslated
        ? true
        : undefined,
      linkups_web_cohort: experimentLinkups ? 'variation' : 'control',
      linkups_counter_flag: experimentLinkups,
      linkups_carousel_flag: experimentLinkups,
      linkups_count_displayed: eventsCarousel.linkupsCountDisplayed.value,
      linkups_available: eventsCarousel.hasLinkupEvents.value,
      og_events_carousel_flag: isOgEventsEligible,
      og_events_count: eventsCarousel.ogEventsCountDisplayed.value,
      events_count_displayed: eventsCarousel.totalEventCount.value,
      og_events_available: eventsCarousel.hasOgEvents.value,
      og_events_web_cohort: experimentPdpOgEvents ? 'variation' : 'control',
      ...getCarouselTrackingSegmentParams(),
      sms_test_id_8: experimentReviewsSummary ? 'variation' : 'control',
      ai_review_summary_flag:
        getPropertyRatingAndReviewsData.value?.propertyReviewsSummary !== '',
      ...houseRulesTracking,
    },
  };

  if (experimentSocialCuesParticipating) {
    trackingInfo.segment = {
      ...trackingInfo.segment,
      social_cues_available: propertyData?.whosGoing?.total > 0,
      social_cues_web_cohort: experimentSocialCues ? 'variation' : 'control',
      social_cues_counter_flag:
        experimentSocialCues && propertyData?.whosGoing?.total > 0,
      social_cues_count_displayed: socialCues.getSocialCuesCount(propertyData?.whosGoing?.total),
    };
  }

  $tracking?.setPageEventContext(houseRulesTracking);
  $tracking?.trackOnPageLoad(trackingInfo.gtm, trackingInfo.segment);

  if (trackingPropertyDescriptionAutoTranslated) {
    $tracking?.onMachineTranslationEvent({ parameter1: gtmPageName });
  }
});

onBeforeUnmount(() => {
  $tracking?.setPageEventContext();
});
</script>

<script>
/* eslint-disable */
import { ca } from "@/lib/channelAttribution.js";

const entryPageNameJs = "PROPERTY";

if (import.meta.client) {
  ca?.setChannelAttribution(null, entryPageNameJs);
}
/* eslint-enable */
</script>

<template>
  <ClientOnly>
    <LazyCommonHwAppDownloadBanner v-bind="getAppDownloadDetails" />
  </ClientOnly>

  <CommonHwHeader :single-bar="true" :sticky="false" class="property-header" />

  <PropertyHwImageGallery
    :image-list="propertyData?.images || []"
    :property-name="propertyData?.name"
    :video-list="propertyData?.videos || []"
    class="hw-wrapper ms-section image-gallery-section"
    :class="{ 'empty-gallery': !galleryHasImages }"
  />

  <PropertyHwHeading
    class="hw-wrapper ms-section"
    v-bind="getPropertyHeadingData"
    @view-more="togglePropertySectionPopup"
    @event-group-click="handleLinkupsEventsClick"
    @whos-staying-click="togglePropertySectionPopup"
  />

  <section class="quick-nav-section">
    <PropertyHwQuickNav
      v-if="!isBelowMediumScreen"
      tag="div"
      class="hw-wrapper ms-section quick-nav-component"
      @view-more="togglePropertySectionPopup"
    />
  </section>

  <section class="hw-wrapper ms-section property-main-section">
    <article class="main-content">
      <PropertyHwAbout
        tag="div"
        class="about-section"
        :description="propertyData?.descriptions?.main"
        @view-more="togglePropertySectionPopup"
      />

      <CommonHwLinkupEvents
        v-if="showPropertyLinkupEventsCarousel"
        ref="propertyLinkupEventsSection"
        tag="div"
        class="linkups-section"
        :event-count="pdpCarouselTotalCount"
        :event-list="pdpCarouselDisplayEvents"
        :property-data="propertyData?.location"
        :event-url="propertyPageUrl"
        :show-og-events-module="isOgEventsEligible"
        :show-filter-pills="pdpCarouselShowFilterPills"
        :active-event-filter="pdpCarouselActiveFilter"
        :og-events-count="pdpCarouselOgEventsCount"
        :linkups-count="pdpCarouselLinkupsCount"
        :carousel-displayed="showPropertyLinkupEventsCarousel"
        :occasion-genius-events="eventsCarousel.occasionGeniusEventsInCarousel.value"
        @filter-click="eventsCarousel.toggleEventFilter"
      />

      <PropertyHwHouseRules
        tag="div"
        class="house-rules-section"
        v-bind="getPropertyHouseRulesData"
        @view-more="togglePropertySectionPopup"
      />

      <PropertyHwFacilities
        v-if="propertyHasFacilities"
        tag="div"
        class="facilities-section"
        v-bind="getPropertyFacilitiesSnippetData"
        @view-more="togglePropertySectionPopup"
      />

      <PropertyHwReviews
        v-if="hasRatingAndReviews"
        tag="div"
        class="rating-and-reviews-section"
        v-bind="getPropertyRatingAndReviewsData"
        @display-modal="togglePropertySectionPopup"
      />

      <PropertyHwMap
        v-if="propertyData?.location"
        tag="div"
        class="location-section"
        :property-location="propertyData.location"
        @display-modal="togglePropertySectionPopup"
      />

      <PropertyHwHostelworldSays
        v-if="propertyData?.hostelworldSays"
        tag="div"
        class="hostelworld-says-section"
        :description="propertyData?.hostelworldSays"
      />
    </article>
    <aside class="availability-side-bar">
      <div class="availability-side-rail">
        <PropertyHwAvailabilityPanel
          class="availability-component"
          :property-id="propertyData?.id"
          :property-slug="propertyData?.urlFriendlyName"
          :property-city="propertyData?.location?.city?.urlFriendlyName"
        />
      </div>
    </aside>
  </section>

  <section class="hw-wrapper ms-section more-properties">
    <PropertyHwMorePropertiesInCity
      tag="div"
      class="more-properties-section"
      :city-data="propertyData?.location"
      :property-list="getMorePropertiesInCityListing"
      :property-type-obj="propertyTypeObj"
    />

    <CityHwAboutSection
      v-if="cityObj && cityObj?.name && cityObj?.description"
      class="city-section"
      :city-name="cityObj?.name"
      :city-description="cityObj?.description"
      :rating-breakdown="cityObj?.ratingBreakdown"
    />
  </section>

  <section class="hw-wrapper ms-section property-breadcrumbs">
    <WdsBreadCrumbs
      :breadcrumbs="crumbs"
      :max-crumbs-shown="getCrumbsShownByViewport"
      :reversed="false"
    />
  </section>

  <LazyPropertyHwReviewsModal
    v-if="isReviewsModalOpen"
    :apply-sheet="isSmallOrMediumScreen"
    :property-name="propertyData?.name"
    v-bind="getPropertyRatingAndReviewsData"
    @close-property-reviews="togglePropertySectionPopup"
  />

  <LazyPropertyHwMapLocationModal
    v-if="isLocationModalOpen && propertyData?.location"
    :apply-sheet="isSmallOrMediumScreen"
    :property-location="propertyData.location"
    :property-type-obj="propertyTypeObj"
    @close-property-location="togglePropertySectionPopup"
  />

  <LazyPropertyHwSocialCuesPopup
    v-if="isSocialCuesModalOpen"
    :apply-sheet="isSmallOrMediumScreen"
    :total-social-cues="propertyData?.whosGoing?.total"
    @close-social-cues="togglePropertySectionPopup"
  />

  <LazyPropertyHwDetailsPopup
    v-if="isPropertyDetailsOpen"
    class="property-details-popup"
    :active-tab="propertyDetailsActiveTab"
    :apply-sheet="isSmallOrMediumScreen"
    :property-type-obj="propertyTypeObj"
    :tabs-data="propertyDetailsTabsData"
    @close-popup="togglePropertySectionPopup"
  />

  <CommonHwLanguagePicker v-show="languageStore.isLanguagePickerOpen" />
</template>

<style lang="scss" scoped>
.property-header {
  margin-bottom: 0;
}

.ms-section {
  margin: 0 auto $wds-spacing-m;
  padding-inline: $wds-spacing-m;

  @include tablet {
    padding-inline: $wds-spacing-l;
  }
}

.image-gallery-section {
  padding-inline: $wds-spacing-m 0;

  &.empty-gallery {
    padding-right: $wds-spacing-m;
  }

  @include tablet {
    padding-inline: $wds-spacing-l 0;

    &.empty-gallery {
      padding-right: $wds-spacing-l;
    }
  }

  @include desktop-large {
    padding-inline: $wds-spacing-l;
  }
}

.heading-section {
  @include tablet {
    padding-top: $wds-spacing-s;
  }
}

.quick-nav-section {
  position: sticky;
  top: 0;
  background-color: $wds-color-ink-lightest;
  z-index: 3;

  .quick-nav-component {
    padding-inline: $wds-spacing-s;
  }
}

.about-section,
.linkups-section,
.house-rules-section,
.facilities-section,
.rating-and-reviews-section,
.location-section,
.hostelworld-says-section,
.more-properties-section {
  :deep(h2) {
    @include title-5-bld;

    margin-bottom: $wds-spacing-m;

    @include desktop {
      @include title-3-bld;
    }
  }

  &::after {
    content: ' ';
    display: block;
    height: 1px;
    width: 100%;
    background-color: $wds-color-ink-lighter;
    margin-block: $wds-spacing-l;
  }
}

.about-section:has(+ .linkups-section) {
  &::after {
    display: none;
  }
}

.linkups-section {
  position: relative;
  margin-block: $wds-spacing-l;

  :deep(h2) {
    margin-bottom: 0;
  }

  &::after {
    display: none;
  }
}

.rating-and-reviews-section,
.location-section,
.hostelworld-says-section,
.more-properties-section {
  &::after {
    height: 0;
    margin-block: 0 $wds-spacing-xxl;
  }
}

.property-main-section {
  margin-top: $wds-spacing-xxl;
  margin-bottom: $wds-spacing-xxl;

  .availability-side-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: $wds-z-index-toast;
  }

  @include tablet-large {
    position: relative;
    display: flex;
    gap: $wds-spacing-l;

    .main-content {
      flex: 1;
      overflow: hidden;
    }

    .availability-side-bar {
      display: block;
      flex-basis: wds-rem(360px);
      position: relative;

      .availability-side-rail {
        position: absolute;
        inset: 0;

        .availability-component {
          position: sticky;
          top: $wds-spacing-xxxl;
        }
      }
    }
  }

  @include desktop-large {
    .availability-side-bar .availability-side-rail {
      inset: wds-rem(-165px) 0 0;
    }
  }
}

.property-details-popup {
  :deep(.modal-container) {
    overflow-y: hidden;

    .modal-body {
      padding: 0;
    }
  }
}
</style>
